import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ShowChart as ShowChartIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import qcService, {
  QcChartFilters,
  QcChartPoint,
  QcChartSummary,
  QcChartDetailResponse,
} from '../../services/qcService';
import clientsService from '../../services/clientsService';
import LeveyJenningsChart from '../../components/qc/LeveyJenningsChart';
import BlankChart from '../../components/qc/BlankChart';
import RpdChart from '../../components/qc/RpdChart';
import WestgardViolationsPanel from '../../components/qc/WestgardViolationsPanel';
import QcInterneTab from '../../components/qc/QcInterneTab';
import UnifiedQaQcTab from '../../components/qc/UnifiedQaQcTab';

const LABS = [
  { value: '', label: 'Tous laboratoires' },
  { value: 'LABORATOIRE_AG', label: 'Laboratoire Ag (Fire Assay / SAA)' },
  { value: 'LABORATOIRE_AU', label: 'Laboratoire Au (Fire Assay / SAA)' },
  { value: 'LABORATOIRE_PHYSICOCHIMIQUE', label: 'Laboratoire Physico-chimique (ICP / WD-XRF)' },
  { value: 'PREP_MECANIQUE', label: 'Préparation mécanique (concassage / broyage)' },
  { value: 'LABORATOIRE_LEGACY', label: 'Données historiques (legacy)' },
];

const CONTROL_TYPES = [
  { value: '', label: 'Tous types' },
  { value: 'BLANK', label: 'Blanc analytique' },
  { value: 'DUPLICATE_RPD', label: 'Duplicata (RPD)' },
  { value: 'CRM_RECOVERY', label: 'MRC (récupération / z-score)' },
  { value: 'PREP_CONCASSAGE_BLANK', label: 'Blanc Concassage (% perte ≤ 15)' },
  { value: 'PREP_BROYAGE_BLANK', label: 'Blanc Broyage (% perte ≤ 30)' },
  { value: 'PREP_CONCASSAGE_QUALITY', label: 'Qualité de concassage (% perte ≤ 15)' },
  { value: 'PREP_BROYAGE_QUALITY', label: 'Qualité de broyage (% perte ≤ 15)' },
];

interface ClientOption {
  id: string;
  company_name: string;
}

const QcControlCharts: React.FC = () => {
  const [clients, setClients] = useState<ClientOption[]>([]);
  // Default window: last 90 days — prevents loading all-time data on first render.
  // Users can widen or clear it from the date pickers.
  const adminDefaultFrom = (() => {
    const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().slice(0, 10);
  })();
  const [filters, setFilters] = useState<QcChartFilters>({ from: adminDefaultFrom });
  const [charts, setCharts] = useState<QcChartSummary[]>([]);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [selectedChartId, setSelectedChartId] = useState<string | null>(null);
  const [detail, setDetail] = useState<QcChartDetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [recomputing, setRecomputing] = useState(false);
  // Page-level tab: 0 = analytical control charts (existing), 1 = instrument-level QC Interne
  const [pageTab, setPageTab] = useState<number>(0);

  const loadClients = useCallback(async () => {
    try {
      const list = await clientsService.getActiveClients();
      setClients(list.map((c) => ({ id: c.id, company_name: c.company_name })));
    } catch (e) {
      // Non-blocking: filter still usable without clients
    }
  }, []);

  const loadCharts = useCallback(async () => {
    setLoadingCharts(true);
    setError(null);
    try {
      const list = await qcService.listCharts(filters);
      setCharts(list);
      if (list.length > 0 && !list.find((c) => c.chart_id === selectedChartId)) {
        setSelectedChartId(list[0].chart_id);
      }
      if (list.length === 0) setSelectedChartId(null);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement des cartes');
    } finally {
      setLoadingCharts(false);
    }
  }, [filters, selectedChartId]);

  const loadDetail = useCallback(async (chartId: string) => {
    setLoadingDetail(true);
    try {
      const d = await qcService.getChartDetail(chartId, {
        from: filters.from,
        to: filters.to,
      });
      setDetail(d);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement de la carte');
    } finally {
      setLoadingDetail(false);
    }
  }, [filters.from, filters.to]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadCharts();
  }, [loadCharts]);

  useEffect(() => {
    if (selectedChartId) loadDetail(selectedChartId);
  }, [selectedChartId, loadDetail]);

  const selectedChart = useMemo(
    () => charts.find((c) => c.chart_id === selectedChartId) || null,
    [charts, selectedChartId],
  );

  const points: QcChartPoint[] = detail?.points || [];

  const handleRecompute = async () => {
    if (!selectedChartId) return;
    setRecomputing(true);
    try {
      await qcService.recomputeChart(selectedChartId);
      await loadCharts();
      await loadDetail(selectedChartId);
    } catch (e: any) {
      setError(e?.message || 'Erreur de recalcul');
    } finally {
      setRecomputing(false);
    }
  };

  const renderChart = () => {
    if (!selectedChart) return null;
    const isPrepBlank =
      selectedChart.control_type === 'PREP_CONCASSAGE_BLANK' ||
      selectedChart.control_type === 'PREP_BROYAGE_BLANK' ||
      selectedChart.control_type === 'PREP_CONCASSAGE_QUALITY' ||
      selectedChart.control_type === 'PREP_BROYAGE_QUALITY';
    const yLabel = isPrepBlank
      ? 'Perte de masse (%)'
      : `${selectedChart.element} ${
          selectedChart.control_type === 'DUPLICATE_RPD'
            ? '(RPD %)'
            : selectedChart.control_type === 'BLANK'
            ? '(valeur blanc)'
            : '(valeur)'
        }`;
    if (selectedChart.control_type === 'DUPLICATE_RPD') {
      return <RpdChart summary={selectedChart} points={points} />;
    }
    if (
      selectedChart.control_type === 'BLANK' ||
      selectedChart.control_type === 'PREP_CONCASSAGE_BLANK' ||
      selectedChart.control_type === 'PREP_BROYAGE_BLANK' ||
      selectedChart.control_type === 'PREP_CONCASSAGE_QUALITY' ||
      selectedChart.control_type === 'PREP_BROYAGE_QUALITY'
    ) {
      // BlankChart draws the value series + a constant UCL band — same shape
      // works for analytical blanks (LOQ ceiling) and prep-mecanique blanks
      // (15 % / 30 % mass-loss ceiling).
      return <BlankChart summary={selectedChart} points={points} yLabel={yLabel} />;
    }
    return <LeveyJenningsChart summary={selectedChart} points={points} yLabel={yLabel} />;
  };

  const prettyChartLabel = (c: QcChartSummary): string => {
    if (c.control_type === 'PREP_CONCASSAGE_BLANK') return 'Blanc Concassage';
    if (c.control_type === 'PREP_BROYAGE_BLANK') return 'Blanc Broyage';
    if (c.control_type === 'PREP_CONCASSAGE_QUALITY') return 'Qualité de concassage';
    if (c.control_type === 'PREP_BROYAGE_QUALITY') return 'Qualité de broyage';
    if (c.control_type === 'DUPLICATE_RPD') return `${c.element} — RPD`;
    if (c.control_type === 'BLANK') return `${c.element} — Blanc`;
    return `${c.element} — MRC ${c.reference_material || ''}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <ShowChartIcon sx={{ fontSize: 32 }} />
          Cartes de contrôle QA/QC
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Suivi statistique (Levey-Jennings, règles de Westgard) pour les blancs, duplicatas et MRC
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={pageTab}
          onChange={(_, v) => setPageTab(v)}
          variant="standard"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab label="Vue unifiée (QA/QC auto)" />
          <Tab label="Cartes de contrôle (échantillons QC)" />
          <Tab label="QC Interne (instrument)" />
        </Tabs>
      </Paper>

      {pageTab === 0 ? (
        <UnifiedQaQcTab />
      ) : pageTab === 2 ? (
        <QcInterneTab />
      ) : (
        <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={clients}
              getOptionLabel={(o) => o.company_name}
              value={clients.find((c) => c.id === filters.client_id) || null}
              onChange={(_, v) => setFilters((f) => ({ ...f, client_id: v?.id }))}
              renderInput={(params) => <TextField {...params} label="Client" />}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Laboratoire</InputLabel>
              <Select
                label="Laboratoire"
                value={filters.laboratory || ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, laboratory: (e.target.value as string) || undefined }))
                }
              >
                {LABS.map((l) => (
                  <MenuItem key={l.value} value={l.value}>
                    {l.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Élément"
              value={filters.element || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, element: e.target.value || undefined }))
              }
              placeholder="Cu, Au, ..."
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Méthode"
              value={filters.method || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, method: e.target.value || undefined }))
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Type de contrôle</InputLabel>
              <Select
                label="Type de contrôle"
                value={filters.control_type || ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, control_type: (e.target.value as any) || undefined }))
                }
              >
                {CONTROL_TYPES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1}>
            <TextField
              size="small"
              fullWidth
              label="Du"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.from || adminDefaultFrom}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value || adminDefaultFrom }))}
            />
          </Grid>
          <Grid item xs={6} md={1}>
            <TextField
              size="small"
              fullWidth
              label="Au"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.to || ''}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value || undefined }))}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '70vh', overflow: 'auto' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {charts.length} cartes
                </Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadCharts}
                  disabled={loadingCharts}
                >
                  Rafraîchir
                </Button>
              </Stack>
            </Box>
            {loadingCharts ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : charts.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Aucune carte ne correspond aux filtres.
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />} sx={{ p: 0 }}>
                {charts.map((c) => {
                  const inControl = (c.in_control_percentage ?? 100) >= 95;
                  return (
                    <Box
                      key={c.chart_id}
                      onClick={() => setSelectedChartId(c.chart_id)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: 'pointer',
                        bgcolor: selectedChartId === c.chart_id ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {prettyChartLabel(c)}
                        </Typography>
                        {inControl ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <WarningIcon fontSize="small" color="warning" />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {c.laboratory.replace('LABORATOIRE_', '').replace('PREP_MECANIQUE', 'Préparation mécanique')} · {c.total_points} pts ·{' '}
                        {c.in_control_percentage?.toFixed(0)}% IC
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {!selectedChart ? (
            <Paper sx={{ p: 4, height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">
                Sélectionnez une carte dans la liste à gauche.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h6">
                        {prettyChartLabel(selectedChart)}
                        {selectedChart.reference_material && selectedChart.control_type === 'CRM_RECOVERY'
                          ? ` (${selectedChart.reference_material})`
                          : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedChart.laboratory === 'PREP_MECANIQUE'
                          ? 'Préparation mécanique'
                          : selectedChart.laboratory}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Recalculer la baseline statistique sur cette carte">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRecompute}
                            disabled={recomputing}
                          >
                            Recalculer baseline
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6} md={3}>
                      <KpiBlock label="Moyenne baseline" value={selectedChart.baseline_mean} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <KpiBlock label="±1σ baseline" value={selectedChart.baseline_std_dev} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <KpiBlock label="Total points" value={selectedChart.total_points} digits={0} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <KpiBlock
                        label="% en contrôle"
                        value={selectedChart.in_control_percentage}
                        suffix=" %"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Paper sx={{ p: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                  <Tab label="Carte" />
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Violations
                        <Chip
                          size="small"
                          color="warning"
                          label={
                            points.filter((p) =>
                              Object.values(p.rule_violations || {}).some(Boolean),
                            ).length
                          }
                        />
                      </Box>
                    }
                  />
                </Tabs>
                {loadingDetail ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : tab === 0 ? (
                  renderChart()
                ) : (
                  <WestgardViolationsPanel points={points} />
                )}
              </Paper>
            </Stack>
          )}
        </Grid>
      </Grid>
        </>
      )}
    </Box>
  );
};

const KpiBlock: React.FC<{
  label: string;
  value: number;
  suffix?: string;
  digits?: number;
}> = ({ label, value, suffix = '', digits = 4 }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6">
      {Number.isFinite(value) ? Number(value).toFixed(digits) : '—'}
      {suffix}
    </Typography>
  </Box>
);

export default QcControlCharts;
